import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FolderPlus, Inbox, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  BUCKET_LABELS,
  BUCKET_ORDER,
  type CorpusBucket,
  type CorpusDocumentRow,
  type CorpusFolder,
} from "@/lib/workspaceCorpus";
import type { PublishType } from "@/lib/workspacePublishTypes";
import { CorpusFolderAccordion, type FolderNode, type FolderDropTarget } from "./CorpusFolderAccordion";
import { CorpusPublishTypesManager } from "./CorpusPublishTypesManager";
import type { Selection } from "./useCorpusDock";

type Props = {
  workspaceId: string;
  selection: Selection;
  setSelection: (s: Selection) => void;
  trees: Record<CorpusBucket, FolderNode[]>;
  docs: CorpusDocumentRow[];
  publishTypes: PublishType[];
  anyTagged: boolean;
  onSetPublishTypes: (rows: PublishType[]) => void;
  onCreateFolder: (bucket: CorpusBucket, parent?: CorpusFolder) => void;
  onRenameRequest: (folder: CorpusFolder) => void;
  onDeleteFolder: (folder: CorpusFolder) => void;
  onToggleGrounding: (folder: CorpusFolder, exclude: boolean) => void;
  onReorderFolder: (folderId: string, target: FolderDropTarget) => void;
  onMoveDocument: (documentId: string, folderId: string | null) => void;
};

const BUCKET_STATE_KEY = (workspaceId: string) => `corpus:buckets:open:${workspaceId}`;
const DOC_MIME = "application/x-excerptus-corpus-document";

export const CorpusBucketSidebar = ({
  workspaceId,
  selection,
  setSelection,
  trees,
  docs,
  publishTypes,
  anyTagged,
  onSetPublishTypes,
  onCreateFolder,
  onRenameRequest,
  onDeleteFolder,
  onToggleGrounding,
  onReorderFolder,
  onMoveDocument,
}: Props) => {
  const selectedFolderId = selection.kind === "folder" ? selection.folderId : null;

  const [openBuckets, setOpenBuckets] = useState<Record<CorpusBucket, boolean>>(() => {
    if (typeof window === "undefined") {
      return { primary: true, secondary: true, supplemental: true };
    }
    try {
      const raw = window.localStorage.getItem(BUCKET_STATE_KEY(workspaceId));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<Record<CorpusBucket, boolean>>;
        return {
          primary: parsed.primary ?? true,
          secondary: parsed.secondary ?? true,
          supplemental: parsed.supplemental ?? true,
        };
      }
    } catch { /* noop */ }
    return { primary: true, secondary: true, supplemental: true };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(BUCKET_STATE_KEY(workspaceId), JSON.stringify(openBuckets));
    } catch { /* noop */ }
  }, [openBuckets, workspaceId]);

  const toggleBucket = (b: CorpusBucket) =>
    setOpenBuckets((prev) => ({ ...prev, [b]: !prev[b] }));

  const [unfiledHover, setUnfiledHover] = useState(false);

  return (
    <aside className="flex w-72 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-2 py-1.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          My Corpus
        </span>
        <div className="flex items-center gap-2">
          <CorpusPublishTypesManager workspaceId={workspaceId} onChanged={onSetPublishTypes} />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="How AI grounding works"
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              <p>
                Ready Corpus documents ground AI generation in your own writing.
                Toggle "Exclude from AI grounding" on a folder to skip its docs.
              </p>
              <p className="mt-1">
                Publish types describe the kinds of content you create. Tag a Corpus doc with its
                type, then star that tag to make the doc a Writing Style Sample the AI imitates
                for that format.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {!anyTagged && publishTypes.length > 0 && docs.length > 0 && (
        <div className="border-b border-border bg-muted/30 px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
          Star a Publish Type tag on a Corpus doc to designate it as a Writing Style Sample.
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-1 py-1.5">
        {BUCKET_ORDER.map((bucket) => {
          const isBucketSelected =
            selection.kind === "folder" &&
            selection.folderId === null &&
            selection.bucket === bucket;
          const isOpen = openBuckets[bucket];
          return (
            <div key={bucket} className="mb-2">
              <div
                className={cn(
                  "group flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold uppercase tracking-wider",
                  isBucketSelected
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-muted/60"
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleBucket(bucket)}
                  className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                  aria-label={isOpen ? `Collapse ${BUCKET_LABELS[bucket]}` : `Expand ${BUCKET_LABELS[bucket]}`}
                >
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  className="flex-1 truncate text-left"
                  onClick={() => setSelection({ kind: "folder", folderId: null, bucket })}
                  onDoubleClick={() => toggleBucket(bucket)}
                >
                  {BUCKET_LABELS[bucket]}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100"
                  aria-label={`New folder in ${BUCKET_LABELS[bucket]}`}
                  onClick={() => onCreateFolder(bucket)}
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {isOpen && (
                <CorpusFolderAccordion
                  bucket={bucket}
                  nodes={trees[bucket]}
                  selectedFolderId={selectedFolderId}
                  onSelect={(folderId) => setSelection({ kind: "folder", folderId, bucket })}
                  onAddSubfolder={(parent) => onCreateFolder(bucket, parent)}
                  onRename={onRenameRequest}
                  onDelete={onDeleteFolder}
                  onToggleGrounding={onToggleGrounding}
                  onReorderFolder={onReorderFolder}
                  onMoveDocument={onMoveDocument}
                />
              )}
            </div>
          );
        })}
        <Separator className="my-2" />
        <button
          type="button"
          onClick={() => setSelection({ kind: "unfiled" })}
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes(DOC_MIME)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setUnfiledHover(true);
          }}
          onDragLeave={() => setUnfiledHover(false)}
          onDrop={(e) => {
            const payload = e.dataTransfer.getData(DOC_MIME);
            setUnfiledHover(false);
            if (!payload) return;
            e.preventDefault();
            try {
              const { id } = JSON.parse(payload) as { id: string };
              if (id) onMoveDocument(id, null);
            } catch { /* noop */ }
          }}
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-sm",
            selection.kind === "unfiled"
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:bg-muted/60",
            unfiledHover && "ring-1 ring-primary/60 bg-primary/5",
          )}
        >
          <Inbox className="h-3.5 w-3.5" />
          Unfiled
          <span className="ml-auto text-[10px]">
            {docs.filter((d) => d.folder_id === null).length}
          </span>
        </button>
      </div>
    </aside>
  );
};
